import { roundMoney, toDecimal } from './money.js';
/**
 * Сумма прописью на русском языке.
 *
 * Обязательный реквизит счёта и УПД. Формат совпадает с 1С:УНФ:
 * «Сорок две тысячи пятьсот рублей 00 копеек» — рубли словами, копейки цифрами.
 *
 * Тонкости, из-за которых нельзя взять готовую библиотеку «для чисел»:
 *  - тысячи женского рода: «одна тысяча», «две тысячи», а не «один», «два»;
 *  - рубли мужского: «один рубль», «два рубля»;
 *  - склонение зависит от последних двух цифр: 11 рублей, но 21 рубль.
 */
const ONES_MASCULINE = [
    '', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять',
];
const ONES_FEMININE = [
    '', 'одна', 'две', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять',
];
const TEENS = [
    'десять', 'одиннадцать', 'двенадцать', 'тринадцать', 'четырнадцать',
    'пятнадцать', 'шестнадцать', 'семнадцать', 'восемнадцать', 'девятнадцать',
];
const TENS = [
    '', '', 'двадцать', 'тридцать', 'сорок', 'пятьдесят',
    'шестьдесят', 'семьдесят', 'восемьдесят', 'девяносто',
];
const HUNDREDS = [
    '', 'сто', 'двести', 'триста', 'четыреста', 'пятьсот',
    'шестьсот', 'семьсот', 'восемьсот', 'девятьсот',
];
const RUBLES = ['рубль', 'рубля', 'рублей'];
const KOPECKS = ['копейка', 'копейки', 'копеек'];
const THOUSANDS = ['тысяча', 'тысячи', 'тысяч'];
const MILLIONS = ['миллион', 'миллиона', 'миллионов'];
const BILLIONS = ['миллиард', 'миллиарда', 'миллиардов'];
const TRILLIONS = ['триллион', 'триллиона', 'триллионов'];
/**
 * Выбрать форму слова по числу.
 * 1, 21, 31 → одна форма; 2-4, 22-24 → вторая; 5-20, 11-14 → третья.
 */
export function pluralize(count, forms) {
    const abs = Math.abs(count);
    const lastTwo = abs % 100;
    if (lastTwo >= 11 && lastTwo <= 14)
        return forms[2];
    const lastOne = abs % 10;
    if (lastOne === 1)
        return forms[0];
    if (lastOne >= 2 && lastOne <= 4)
        return forms[1];
    return forms[2];
}
/** Триада 0..999 словами. */
function tripletToWords(value, gender) {
    const words = [];
    const hundreds = Math.floor(value / 100);
    const remainder = value % 100;
    const tens = Math.floor(remainder / 10);
    const ones = remainder % 10;
    if (hundreds > 0)
        words.push(HUNDREDS[hundreds]);
    if (tens === 1) {
        // 10..19 — отдельные слова, не раскладываются на «десять» + «один»
        words.push(TEENS[ones]);
    }
    else {
        if (tens > 1)
            words.push(TENS[tens]);
        if (ones > 0) {
            words.push(gender === 'f' ? ONES_FEMININE[ones] : ONES_MASCULINE[ones]);
        }
    }
    return words;
}
/**
 * Целое число прописью.
 * gender — род существительного, к которому относится число («один рубль» / «одна тысяча»).
 */
export function integerToWords(value, gender = 'm') {
    if (!Number.isFinite(value))
        throw new RangeError('Ожидалось конечное число');
    const int = Math.trunc(Math.abs(value));
    if (int === 0)
        return 'ноль';
    // Разбиваем на триады, начиная с младшей
    const triplets = [];
    let rest = int;
    while (rest > 0) {
        triplets.push(rest % 1000);
        rest = Math.floor(rest / 1000);
    }
    if (triplets.length > 5) {
        throw new RangeError('Сумма слишком велика для записи прописью');
    }
    // Разряды от младшего к старшему. Тысячи — женского рода.
    const scales = [
        { forms: null, gender },
        { forms: THOUSANDS, gender: 'f' },
        { forms: MILLIONS, gender: 'm' },
        { forms: BILLIONS, gender: 'm' },
        { forms: TRILLIONS, gender: 'm' },
    ];
    const words = [];
    for (let i = triplets.length - 1; i >= 0; i -= 1) {
        const triplet = triplets[i];
        if (triplet === 0)
            continue;
        const scale = scales[i];
        words.push(...tripletToWords(triplet, scale.gender));
        if (scale.forms)
            words.push(pluralize(triplet, scale.forms));
    }
    const result = words.join(' ');
    return value < 0 ? `минус ${result}` : result;
}
/**
 * Сумма прописью для печатной формы.
 *
 * @example
 * amountInWords(42500)      // «Сорок две тысячи пятьсот рублей 00 копеек»
 * amountInWords(1234.56)    // «Одна тысяча двести тридцать четыре рубля 56 копеек»
 */
export function amountInWords(value, options = {}) {
    const { kopecksInWords = false, capitalize = true, withCurrency = true } = options;
    const rounded = roundMoney(toDecimal(value));
    const isNegative = rounded.isNegative();
    const absolute = rounded.abs();
    const rubles = absolute.floor().toNumber();
    // Через строку, а не умножением: 1234.56 * 100 в двоичной арифметике даёт 1234.5599...
    const kopecks = Number(absolute.toFixed(2).split('.')[1] ?? '0');
    const parts = [];
    if (isNegative)
        parts.push('минус');
    parts.push(integerToWords(rubles, 'm'));
    if (withCurrency)
        parts.push(pluralize(rubles, RUBLES));
    if (kopecksInWords) {
        parts.push(integerToWords(kopecks, 'f'));
    }
    else {
        parts.push(String(kopecks).padStart(2, '0'));
    }
    if (withCurrency)
        parts.push(pluralize(kopecks, KOPECKS));
    const result = parts.join(' ');
    return capitalize ? result.charAt(0).toUpperCase() + result.slice(1) : result;
}
/**
 * Количество прописью — для оборотной стороны доверенности М-2,
 * где количество ТМЦ печатается словами.
 */
export function quantityInWords(value, unitForms) {
    const int = toDecimal(value).floor().toNumber();
    const words = integerToWords(int, 'f');
    const result = unitForms ? `${words} ${pluralize(int, unitForms)}` : words;
    return result.charAt(0).toUpperCase() + result.slice(1);
}
//# sourceMappingURL=amount-in-words.js.map